import { useCallback, useEffect, useRef, useState } from 'react';
import { setupPWA } from './pwa';

/**
 * Wires up the PWA state a shell needs to show: whether an update is
 * waiting and whether the browser is online. Installing is
 * `src/data/install.ts`, which has to start before any component does.
 */
export function useAppShell() {
  const [updateReady, setUpdateReady] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);

  const applyUpdate = useRef<((reload?: boolean) => Promise<void>) | null>(
    null,
  );

  useEffect(() => {
    applyUpdate.current = setupPWA({
      onNeedRefresh: () => setUpdateReady(true),
      onOfflineReady: () => setOfflineReady(true),
    });
  }, []);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const update = useCallback(() => {
    void applyUpdate.current?.(true);
  }, []);

  return {
    updateReady,
    offlineReady,
    online,
    update,
    dismissOfflineNotice: () => setOfflineReady(false),
  };
}
